'use client';

import { useState, useMemo } from 'react';
import KioskNav from '../shared/KioskNav';
import { getVehicleSuggestions, getVehicleInfo } from '../../data/mock-data';

interface VehicleInfoPageProps {
  initialVehicleNumber: string;
  initialContact: string;
  initialDriverName: string;
  onVehicleNumberChange: (value: string) => void;
  onContactChange: (value: string) => void;
  onDriverNameChange: (value: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}

export default function VehicleInfoPage({
  initialVehicleNumber,
  initialContact,
  initialDriverName,
  onVehicleNumberChange,
  onContactChange,
  onDriverNameChange,
  onPrevious,
  onNext,
}: VehicleInfoPageProps) {
  const [vehicleNumber, setVehicleNumber] = useState(initialVehicleNumber);
  const [contact, setContact] = useState(initialContact || '010-');
  const [driverName, setDriverName] = useState(initialDriverName);
  const [hasSelected, setHasSelected] = useState(false);

  // props 변경 시 동기화 (렌더 중 조정 — effect 내 setState 회피)
  const [prevProps, setPrevProps] = useState({ initialVehicleNumber, initialContact, initialDriverName });
  if (
    prevProps.initialVehicleNumber !== initialVehicleNumber ||
    prevProps.initialContact !== initialContact ||
    prevProps.initialDriverName !== initialDriverName
  ) {
    setPrevProps({ initialVehicleNumber, initialContact, initialDriverName });
    setVehicleNumber(initialVehicleNumber);
    setContact(initialContact || '010-');
    setDriverName(initialDriverName);
  }

  // 차량번호 자동완성 (입력값에서 파생)
  const suggestions = useMemo(
    () => (!hasSelected && vehicleNumber.length >= 2 ? getVehicleSuggestions(vehicleNumber) : []),
    [vehicleNumber, hasSelected]
  );

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (value.startsWith('010-')) {
      const digits = value.slice(4).replace(/\D/g, '');

      if (digits.length < 4) {
        value = '010-' + digits;
      } else if (digits.length === 4) {
        value = '010-' + digits + '-';
      } else if (digits.length <= 8) {
        value = '010-' + digits.slice(0, 4) + '-' + digits.slice(4);
      } else {
        value = '010-' + digits.slice(0, 4) + '-' + digits.slice(4, 8);
      }
    } else {
      value = '010-';
    }

    setContact(value);
    onContactChange(value);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setVehicleNumber(suggestion);
    onVehicleNumberChange(suggestion);
    setHasSelected(true);

    // 차량 정보 자동 입력
    const vehicleInfo = getVehicleInfo(suggestion);
    if (vehicleInfo) {
      setDriverName(vehicleInfo.driver_name);
      onDriverNameChange(vehicleInfo.driver_name);
      setContact(vehicleInfo.driver_phone);
      onContactChange(vehicleInfo.driver_phone);
    }
  };

  const handleVehicleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVehicleNumber(e.target.value);
    onVehicleNumberChange(e.target.value);
    setHasSelected(false);
  };

  const handleDriverNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDriverName(e.target.value);
    onDriverNameChange(e.target.value);
  };

  const isFormValid = vehicleNumber.trim() && contact.length >= 13 && driverName.trim();

  return (
    <div className="h-full bg-gray-50 flex flex-col justify-between py-6 px-8">
      {/* Header */}
      <header className="text-center">
        <h1 className="text-5xl font-bold text-gray-800">출고차량 정보</h1>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center space-y-6">
        {/* Vehicle Number with Autocomplete */}
        <div className="flex items-center w-full max-w-3xl">
          <label className="text-3xl font-medium text-gray-800 w-56 text-right pr-6">차량번호 :</label>
          <div className="flex-1 max-w-sm relative">
            <input
              type="text"
              value={vehicleNumber}
              onChange={handleVehicleNumberChange}
              placeholder="서울 12가 3456"
              className="w-full px-6 py-4 text-3xl bg-green-100 border-2 border-green-300 rounded-lg focus:outline-none focus:border-green-500 text-center"
            />

            {/* 자동완성 드롭다운 */}
            {suggestions.length > 0 && (
              <div
                className="absolute top-full mt-2 w-full bg-white border-2 border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto"
                onMouseDown={(e) => e.preventDefault()}
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full px-4 py-3 text-xl text-left hover:bg-blue-50 border-b border-gray-200 last:border-b-0"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="flex items-center w-full max-w-3xl">
          <label className="text-3xl font-medium text-gray-800 w-56 text-right pr-6">연락처 :</label>
          <input
            type="text"
            value={contact}
            onChange={handleContactChange}
            placeholder="010-1234-5678"
            maxLength={13}
            className="flex-1 max-w-sm px-6 py-4 text-3xl bg-green-100 border-2 border-green-300 rounded-lg focus:outline-none focus:border-green-500 text-center"
          />
        </div>

        {/* Driver Name */}
        <div className="flex items-center w-full max-w-3xl">
          <label className="text-3xl font-medium text-gray-800 w-56 text-right pr-6">기사님 성함 :</label>
          <input
            type="text"
            value={driverName}
            onChange={handleDriverNameChange}
            placeholder="홍길동"
            className="flex-1 max-w-sm px-6 py-4 text-3xl bg-green-100 border-2 border-green-300 rounded-lg focus:outline-none focus:border-green-500 text-center"
          />
        </div>
      </main>

      {/* Bottom Section */}
      <div className="flex flex-col items-center space-y-4">
        {/* Navigation Buttons */}
        <KioskNav
          onPrevious={onPrevious}
          onNext={onNext}
          nextDisabled={!isFormValid}
        />

        {/* Bottom Instructions */}
        <p className="text-lg text-gray-600">터치스크린을 사용하여 입력해주세요</p>
      </div>
    </div>
  );
}
